import type { Permission, Role } from '@/types/auth';

/**
 * Core permission checking utilities used by both client and server code
 */

export function checkHasPermission(
  userPermissions: Set<Permission> | ReadonlySet<Permission>,
  requiredPermission?: Permission | null
): boolean {
  if (!requiredPermission) {
    return false;
  }

  return userPermissions.has(requiredPermission);
}

export function checkHasRole(
  userRoles: Set<Role> | ReadonlySet<Role>,
  requiredRole?: Role | null
): boolean {
  if (!requiredRole) {
    return false;
  }

  return userRoles.has(requiredRole);
}

export function checkHasAnyPermission(
  userPermissions: Set<Permission> | ReadonlySet<Permission>,
  requiredPermissions: readonly Permission[] = []
): boolean {
  if (!requiredPermissions || requiredPermissions.length === 0) {
    return false;
  }

  return requiredPermissions.some((p) => userPermissions.has(p));
}

export function checkHasAllPermissions(
  userPermissions: Set<Permission> | ReadonlySet<Permission>,
  requiredPermissions: readonly Permission[] = []
): boolean {
  if (!requiredPermissions || requiredPermissions.length === 0) {
    return true;
  }

  return requiredPermissions.every((p) => userPermissions.has(p));
}

/**
 * Checks if the user has at least one of the specified roles.
 */
export function checkHasAnyRole(
  userRoles: Set<Role> | ReadonlySet<Role>,
  requiredRoles: readonly Role[] = []
): boolean {
  if (!requiredRoles || requiredRoles.length === 0) {
    return false;
  }

  return requiredRoles.some((r) => userRoles.has(r));
}
